import { AlertTriangle, ChevronRight } from 'lucide-react'
import { Input } from '@/core/ui/Input'
import { cn } from '@/lib/cn'

/**
 * Primitivos de formulário da parte diária. Cópia dos helpers locais do
 * carreteiro (step0/step1/ViagemFormSheet) — extrair para `features/common`
 * quando um terceiro módulo precisar.
 */

export function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-2">
      <h3 className="text-xs font-bold text-accent uppercase">{title}</h3>
      {children}
    </section>
  )
}

export function Pair({ children }: { children: React.ReactNode }) {
  return <div className="grid grid-cols-2 gap-3">{children}</div>
}

export function Alerta({ tone, children }: { tone: 'warn' | 'bad' | 'info'; children: React.ReactNode }) {
  return (
    <div
      className={cn(
        'rounded-lg border px-4 py-3 flex items-start gap-3',
        tone === 'bad' ? 'border-bad bg-bad/10' : tone === 'warn' ? 'border-warn bg-warn/10' : 'border-border bg-bg-elevated',
      )}
    >
      <AlertTriangle className={cn('h-5 w-5 shrink-0 mt-0.5', tone === 'bad' ? 'text-bad' : tone === 'warn' ? 'text-warn' : 'text-on-surface-dim')} />
      <div className="text-sm text-on-surface min-w-0">{children}</div>
    </div>
  )
}

export function ReadOnlyField({ label, value, hint }: { label: string; value: string; hint?: string | null }) {
  return (
    <div className="w-full card-surface-variant px-4 py-3">
      <p className="text-xs font-medium text-on-surface-dim uppercase">{label}</p>
      <p className="text-on-surface mt-0.5 truncate">{value}</p>
      {hint && <p className="text-xs text-on-surface-dim mt-0.5">{hint}</p>}
    </div>
  )
}

export function PickerField({
  label,
  value,
  sublabel,
  badge,
  onClick,
  disabled,
  erro,
  destacado,
}: {
  label: string
  value: string
  sublabel?: string | null
  badge?: string | null
  onClick: () => void
  disabled?: boolean
  erro?: string | null
  destacado?: boolean
}) {
  return (
    <div>
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        className={cn(
          'w-full card-surface-variant text-left px-4 py-3 flex items-center gap-3 min-h-touch hover:bg-surface-2/80 transition-colors',
          'disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent',
          (erro || destacado) && 'ring-1 ring-bad',
        )}
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-xs font-medium text-on-surface-dim uppercase">{label}</p>
            {badge && (
              <span className="text-[10px] uppercase tracking-wide bg-accent/15 text-accent px-1.5 py-0.5 rounded">{badge}</span>
            )}
          </div>
          <p className="text-on-surface mt-0.5 truncate">{value}</p>
          {sublabel && <p className="text-xs text-on-surface-dim mt-0.5 truncate">{sublabel}</p>}
        </div>
        <ChevronRight className="h-5 w-5 text-on-surface-dim" />
      </button>
      {erro && <p className="text-xs text-bad mt-1">{erro}</p>}
    </div>
  )
}

export function LabeledInput({
  label,
  value,
  onChange,
  placeholder,
  type = 'text',
  maxLength,
  inputMode,
  min,
  max,
  erro,
  destacado,
}: {
  label: string
  value: string
  onChange: (s: string) => void
  placeholder?: string
  type?: string
  maxLength?: number
  inputMode?: 'numeric' | 'text' | 'decimal'
  min?: string
  max?: string
  erro?: string | null
  destacado?: boolean
}) {
  return (
    <div>
      <label className="text-xs font-medium text-on-surface-dim uppercase">{label}</label>
      <Input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        maxLength={maxLength}
        inputMode={inputMode}
        min={min}
        max={max}
        className={cn('mt-1', (erro || destacado) && 'ring-1 ring-bad')}
      />
      {erro && <p className="text-xs text-bad mt-1">{erro}</p>}
    </div>
  )
}

export function NumericField({
  label,
  value,
  onChange,
  integer,
  placeholder,
  erro,
  destacado,
}: {
  label: string
  value: string
  onChange: (s: string) => void
  integer?: boolean
  placeholder?: string
  erro?: string | null
  destacado?: boolean
}) {
  const pattern = integer ? /^[0-9]*$/ : /^[0-9]*[.,]?[0-9]*$/
  return (
    <div>
      <label className="text-xs font-medium text-on-surface-dim uppercase">{label}</label>
      <Input
        type="text"
        inputMode={integer ? 'numeric' : 'decimal'}
        value={value}
        placeholder={placeholder}
        onChange={(e) => {
          const v = e.target.value
          if (v === '' || pattern.test(v)) onChange(integer ? v : v.replace(',', '.'))
        }}
        className={cn('mt-1', (erro || destacado) && 'ring-1 ring-bad')}
      />
      {erro && <p className="text-xs text-bad mt-1">{erro}</p>}
    </div>
  )
}
